import { Flex, Text, Tooltip } from '@chakra-ui/react';
import {
  faEnvelope,
  faInfoCircle,
  faUser,
  faUserPlus
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  Button,
  ButtonIcon,
  Checkbox,
  FormFeedback,
  useToast
} from '@morressier/alembic';
import { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import {
  fetchUserDetailsById,
  IUserDetailsResponse,
  useAddUserMutation,
  useUpdateUserByAdminMutation
} from 'src/api/signUpMutation';
import { fetchWorkspaceJournals } from 'src/api/queries';
import ChakraMultiSelect from 'src/components/MultiSelect';
import {
  buildRowDuplicateMessage,
  findRowLevelDuplicates,
  IOrganizationForm,
  useUserDetailsData
} from '../../UserRegistrationSteps/StepThree/signupHook';
import {
  IReviewerOrganizationForm,
  ISpecialtyKeywordData
} from '@publishing-engine/domain/models';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { getConfigProperties } from '../../UserRegistrationSteps/StepThree/SpecialtyKeywordTableRow';
import { CustomErrorMessage } from '../../UserRegistrationSteps/StepThree/StepThree';
import ProfileDetails from '../../UserRegistrationSteps/StepThree/ProfileDetails';
import { PhoneFieldData } from '../../FormField/common/PhoneNumberInput';
import ResearchFocusAndExpertise from '../../UserRegistrationSteps/StepThree/ResearchFocusAndExpertise';

import AffiliationSection from './AffiliationSection';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import NavbarDashboard from 'src/components/NavbarDashboard';

export type IUserForm = {
  prefix: string;
  firstName: string;
  middleName: string;
  lastName: string;
  primaryEmail: string;
  secondaryEmail?: string;
  email: string;
  ccEmail?: string;
  isSameAsSecondaryEmail?: boolean;
  orcid?: string;
  degree?: string;
  jobTitle?: string;
  profileDetails: {
    linkedinProfileUrl?: string;
    phoneNumber?: PhoneFieldData;
  };
  researchFocusAndExpertise: {
    reviewerInterest: string;
    unavailabilityPeriodFrom: string;
    unavailabilityPeriodTo: string;
    specialtyAndKeywords: ISpecialtyKeywordData[];
    scandinavianKeywords: string[];
    scandinavianKeywordsTemp?: Array<{ text: string }>;
    professionalInterests?: string[];
    professionalInterestsTemp?: Array<{ text: string }>;
  };
  affiliationDetails: IOrganizationForm;
  additionalAffiliations: IOrganizationForm[];
  addToReviewerSearchDatabase: boolean;
  submissionContextIds: string[];
};
interface AddUserModalProps {
  organizationId: string;
  workspaceId?: string;
  submissionContextId?: string;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  submitButtonText: string;
  onSuccess?: (userId: string, email: string) => void;
  email?: string;
  mode?: 'add' | 'edit';
  userId?: string;
}

const getEmptyFormValues = (
  email?: string,
  submissionContextId?: string
): IUserForm => ({
  prefix: '',
  firstName: '',
  middleName: '',
  lastName: '',
  primaryEmail: email ?? '',
  email: email ?? '',
  affiliationDetails: {} as IOrganizationForm,
  researchFocusAndExpertise: {
    reviewerInterest: '',
    unavailabilityPeriodFrom: '',
    unavailabilityPeriodTo: '',
    specialtyAndKeywords: [],
    scandinavianKeywords: [],
    scandinavianKeywordsTemp: [],
    professionalInterests: [],
    professionalInterestsTemp: []
  },
  additionalAffiliations: [],
  addToReviewerSearchDatabase: Boolean(submissionContextId),
  submissionContextIds: submissionContextId ? [submissionContextId] : [],
  profileDetails: {
    phoneNumber: {
      fullNumber: '',
      value: '',
      countryCallingCode: '',
      country: ''
    },
    linkedinProfileUrl: ''
  },
  jobTitle: ''
});

const mapUserDetailsToFormValues = (data?: IUserDetailsResponse): IUserForm => {
  const phoneNumber = data?.profileDetails?.phoneNumber ?? '';
  const countryCallingCode = data?.profileDetails?.countryCallingCode ?? '';
  const countryCode = data?.profileDetails?.countryCode ?? '';
  const fullNumber =
    phoneNumber && countryCallingCode
      ? `+${countryCallingCode}${phoneNumber}`
      : '';

  return {
    prefix: data?.user?.prefix ?? '',
    firstName: data?.user?.firstName ?? '',
    middleName: data?.user?.middleName ?? '',
    lastName: data?.user?.lastName ?? '',
    primaryEmail: data?.user?.email ?? '',
    email: data?.user?.email ?? '',
    secondaryEmail: data?.user?.secondaryEmail ?? '',
    ccEmail: data?.user?.ccEmail ?? '',
    isSameAsSecondaryEmail: data?.user?.isSameAsSecondaryEmail ?? false,
    orcid: data?.user?.orcid ?? '',
    degree: data?.user?.degree ?? '',
    jobTitle: data?.user?.jobTitle,
    profileDetails: {
      linkedinProfileUrl: data?.profileDetails?.linkedinProfileUrl ?? '',
      phoneNumber: {
        fullNumber,
        value: phoneNumber,
        countryCallingCode,
        country: countryCode
      }
    },
    affiliationDetails: (data?.affiliationDetails ?? {}) as IOrganizationForm,
    additionalAffiliations:
      (data?.additionalAffiliations as IOrganizationForm[]) ?? [],
    addToReviewerSearchDatabase: false,
    submissionContextIds: [],
    researchFocusAndExpertise: {
      reviewerInterest: data?.researchFocusAndExpertise?.reviewerInterest ?? '',
      unavailabilityPeriodFrom:
        data?.researchFocusAndExpertise?.unavailabilityPeriodFrom ?? '',
      unavailabilityPeriodTo:
        data?.researchFocusAndExpertise?.unavailabilityPeriodTo ?? '',
      specialtyAndKeywords:
        data?.researchFocusAndExpertise?.specialtyAndKeywords?.map(item => ({
          specialty: item.specialtyId
            ? {
                termId: item.specialtyId,
                name: item.specialtyName ?? ''
              }
            : null,
          subSpecialty: item.subSpecialtyId
            ? {
                termId: item.subSpecialtyId,
                name: item.subSpecialtyName ?? ''
              }
            : null,
          keyword: item.keywordId
            ? {
                termId: item.keywordId,
                name: item.keyword ?? ''
              }
            : null,
          scandinavianKeyword: item.scandinavianKeywordId
            ? {
                termId: item.scandinavianKeywordId,
                name: item.scandinavianKeyword ?? ''
              }
            : null,
          isCustomKeyword: item.keywordId?.startsWith('custom_') ?? false
        })) ?? [],
      scandinavianKeywords:
        data?.researchFocusAndExpertise?.scandinavianKeywords ?? [],
      scandinavianKeywordsTemp:
        data?.researchFocusAndExpertise?.scandinavianKeywords?.map(value => ({
          text: value
        })) ?? [],
      professionalInterests:
        data?.researchFocusAndExpertise?.professionalInterests ?? [],
      professionalInterestsTemp:
        data?.researchFocusAndExpertise?.professionalInterests?.map(value => ({
          text: value
        })) ?? []
    }
  };
};

const buildUserPayload = (
  formValues?: Partial<IUserForm>,
  resolvedPrimaryEmail?: string
) => {
  const phoneNumberValue =
    formValues?.profileDetails?.phoneNumber?.value?.trim() ?? '';
  const countryCallingCode = phoneNumberValue
    ? formValues?.profileDetails?.phoneNumber?.countryCallingCode ?? ''
    : '';
  const countryCode = phoneNumberValue
    ? formValues?.profileDetails?.phoneNumber?.country ?? ''
    : '';

  const researchFocusAndExpertise = formValues?.researchFocusAndExpertise;
  const updatedAffiliationDetails = {
    ...(formValues?.affiliationDetails ?? ({} as IOrganizationForm))
  };
  delete updatedAffiliationDetails.selectedAffiliation;

  const specialtyAndKeywords =
    researchFocusAndExpertise?.specialtyAndKeywords
      ?.filter(item => item.specialty !== null || item.keyword !== null)
      .map(item => ({
        specialtyName: item.specialty?.name ?? '',
        specialtyId: item.specialty?.termId ?? '',
        subSpecialtyName: item.subSpecialty?.name ?? '',
        subSpecialtyId: item.subSpecialty?.termId ?? '',
        keyword: item.keyword?.name ?? '',
        keywordId: item.keyword?.termId ?? '',
        scandinavianKeyword: item.scandinavianKeyword?.name ?? '',
        scandinavianKeywordId: item.scandinavianKeyword?.termId ?? ''
      })) ?? [];

  return {
    prefix: formValues?.prefix ?? '',
    firstName: formValues?.firstName ?? '',
    middleName: formValues?.middleName ?? '',
    lastName: formValues?.lastName ?? '',
    email: resolvedPrimaryEmail ?? formValues?.email ?? '',
    degree: formValues?.degree ?? '',
    jobTitle: formValues?.jobTitle,
    orcid: formValues?.orcid ?? '',
    secondaryEmail: formValues?.secondaryEmail ?? '',
    ccEmail: formValues?.ccEmail ?? '',
    profileDetails: {
      phoneNumber: phoneNumberValue,
      countryCallingCode,
      countryCode,
      linkedinProfileUrl: formValues?.profileDetails?.linkedinProfileUrl ?? ''
    },
    affiliationDetails: updatedAffiliationDetails,
    additionalAffiliations: formValues?.additionalAffiliations ?? [],
    submissionContextIds: formValues?.addToReviewerSearchDatabase
      ? formValues?.submissionContextIds ?? []
      : [],
    researchFocusAndExpertise: {
      reviewerInterest: researchFocusAndExpertise?.reviewerInterest ?? '',
      unavailabilityPeriodFrom:
        researchFocusAndExpertise?.reviewerInterest === 'Yes'
          ? researchFocusAndExpertise?.unavailabilityPeriodFrom ?? ''
          : '',
      unavailabilityPeriodTo:
        researchFocusAndExpertise?.reviewerInterest === 'Yes'
          ? researchFocusAndExpertise?.unavailabilityPeriodTo ?? ''
          : '',
      specialtyAndKeywords,

      scandinavianKeywords:
        researchFocusAndExpertise?.scandinavianKeywords ?? [],

      professionalInterests:
        researchFocusAndExpertise?.professionalInterests ?? []
    }
  };
};

const AddUser = ({
  organizationId,
  workspaceId,
  submissionContextId,
  isOpen,
  onClose,
  title,
  submitButtonText,
  onSuccess,
  email,
  mode = 'add',
  userId
}: AddUserModalProps) => {
  const toast = useToast();
  const lastScrolledSubmit = useRef<number | null>(null);
  const initialEditPayloadRef = useRef<ReturnType<
    typeof buildUserPayload
  > | null>(null);

  const methods = useForm<IUserForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: getEmptyFormValues(email, submissionContextId)
  });

  const addUserMutation = useAddUserMutation();
  const updateUserByAdminMutation = useUpdateUserByAdminMutation();
  const isEditMode = mode === 'edit';

  const [customErrorMessage, setCustomErrorMessage] =
    useState<CustomErrorMessage>({
      orcid: false
    });

  const [openIndices, setOpenIndices] = useState<number[]>([0, 1, 2]);

  const { configs } = useUserDetailsData(organizationId || '');
  const { data: userDetailsResponse, isLoading: isUserDetailsLoading } =
    useQuery({
      queryKey: ['user-details', userId],
      queryFn: () => (userId ? fetchUserDetailsById(userId) : null),
      enabled: isOpen && isEditMode && !!userId,
      retry: 0
    });
  const userDetails = userDetailsResponse?.data;

  const { data: workspaceJournals } = useQuery({
    queryKey: ['workspace-journals', workspaceId],
    queryFn: () =>
      workspaceId ? fetchWorkspaceJournals(workspaceId, 0, 999) : null,
    enabled: isOpen && !isEditMode && !!workspaceId && !submissionContextId
  });

  const journalOptions = (workspaceJournals?.collection ?? []).map(journal => ({
    label: journal.data.name,
    value: journal.id
  }));
  const singleJournalId =
    journalOptions.length === 1 ? journalOptions[0]?.value : undefined;
  const singleJournalName =
    journalOptions.length === 1 ? journalOptions[0]?.label : undefined;

  const {
    register,
    formState,
    trigger,
    getValues,
    handleSubmit,
    watch,
    setValue
  } = methods;
  const { errors, isSubmitted, submitCount, isValid } = formState;
  const watchedFormValues = watch();
  const addToReviewerSearchDatabase = watch('addToReviewerSearchDatabase');
  const submissionContextIds = watch('submissionContextIds');

  const validateAffiliation = (
    value: IReviewerOrganizationForm
  ): string | true => {
    if (!value || Object.keys(value).length === 0 || !value.organizationName) {
      return 'Primary affiliation is required';
    }
    return true;
  };

  const validateSpecialtyAndKeywords = (value: any[]): string | true => {
    const seen = new Set<string>();
    if (value) {
      for (const item of value) {
        if (!item.specialty || !item.keyword) continue;
        const key = `${item.specialty.termId}-${item.keyword.termId}`;
        if (seen.has(key)) {
          return 'Duplicate Specialty Keyword not allowed';
        }
        seen.add(key);
      }
    }
    if (!isSpecialtyAndKeywordRequired) return true;
    if (
      !Array.isArray(value) ||
      value.length === 0 ||
      !value.some((item: any) => item?.specialty)
    ) {
      return 'At least one specialty is required';
    }
    return true;
  };

  register('affiliationDetails', {
    validate: validateAffiliation
  });
  register('researchFocusAndExpertise.specialtyAndKeywords', {
    validate: validateSpecialtyAndKeywords
  });
  register('addToReviewerSearchDatabase');
  register('submissionContextIds', {
    validate: (value: string[]) => {
      if (
        addToReviewerSearchDatabase &&
        (!Array.isArray(value) || value.length === 0)
      ) {
        return 'Select at least one journal';
      }
      return true;
    }
  });

  const getResolvedPrimaryEmail = (formValues?: Partial<IUserForm>) =>
    formValues?.email ?? userDetails?.user?.email ?? email ?? '';

  const checkIsSpecialtyAndKeywordIsRequired = () => {
    if (configs?.Specialty && configs?.Keyword) {
      const specialtyConfig = getConfigProperties(configs?.Specialty);

      const keywordConfig = getConfigProperties(configs?.Keyword);

      return (
        specialtyConfig.validations.required ||
        keywordConfig.validations.required
      );
    }
    return false;
  };

  const resetForm = () => {
    if (isEditMode && userDetails) {
      const initialFormValues = mapUserDetailsToFormValues(userDetails);
      methods.reset(initialFormValues);
      initialEditPayloadRef.current = buildUserPayload(
        initialFormValues,
        getResolvedPrimaryEmail(initialFormValues)
      );
      return;
    }

    methods.reset(getEmptyFormValues(email, submissionContextId));
    initialEditPayloadRef.current = null;
  };

  const isSpecialtyAndKeywordRequired = checkIsSpecialtyAndKeywordIsRequired();
  const hasEditPayloadChanges =
    isEditMode &&
    !!initialEditPayloadRef.current &&
    !isEqual(
      initialEditPayloadRef.current,
      buildUserPayload(
        watchedFormValues,
        getResolvedPrimaryEmail(watchedFormValues)
      )
    );

  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode) {
      if (userDetails) {
        const initialFormValues = mapUserDetailsToFormValues(userDetails);
        methods.reset(initialFormValues);
        initialEditPayloadRef.current = buildUserPayload(
          initialFormValues,
          userDetails?.user?.email ?? email ?? initialFormValues.email ?? ''
        );
      }
      return;
    }

    methods.reset(getEmptyFormValues(email, submissionContextId));
    initialEditPayloadRef.current = null;
  }, [isOpen, isEditMode, userDetails, email, methods, submissionContextId]);

  const handleCreateUser = async (emailNotify: boolean = false) => {
    const isValid = await trigger();
    if (!isValid) {
      scrollToFirstError();
      return;
    }

    const liveData =
      getValues('researchFocusAndExpertise.specialtyAndKeywords') ||
      watch('researchFocusAndExpertise.specialtyAndKeywords') ||
      [];
    const rowDuplicateResult = findRowLevelDuplicates(liveData);

    if (rowDuplicateResult.hasDuplicates) {
      toast({
        variant: 'error',
        title: 'Duplicate entry detected',
        description: (
          <Flex direction="column">
            {rowDuplicateResult.duplicateRows.map(issue => (
              <Text
                key={`${issue.rowIndex}-${issue.value}-${issue.fieldKeys.join(
                  '-'
                )}`}
              >
                {buildRowDuplicateMessage(issue)}
              </Text>
            ))}
          </Flex>
        ),
        isClosable: true
      });

      return;
    }
    const formValues = getValues();
    const resolvedPrimaryEmail = getResolvedPrimaryEmail(formValues);
    const payload = buildUserPayload(formValues, resolvedPrimaryEmail);

    const response = isEditMode
      ? await updateUserByAdminMutation.mutateAsync({
          payload: {
            ...payload,
            userId: userId ?? ''
          },
          emailNotify,
          workspaceId: workspaceId ?? ''
        })
      : await addUserMutation.mutateAsync({
          payload: payload,
          emailNotify,
          workspaceId: workspaceId ?? ''
        });

    if (!response.isSuccess) {
      const message =
        typeof response?.message === 'string'
          ? response.message
          : response?.message?.message || 'Internal Server Error';

      toast({
        variant: 'error',
        title: 'Error',
        description: message,
        isClosable: true
      });
    } else {
      toast({
        variant: 'success',
        title: title,
        description: response?.message,
        isClosable: true
      });
      if (!isEditMode) {
        resetForm();
      }
      onClose();
      onSuccess?.(response?.userId, resolvedPrimaryEmail);
    }
  };

  const handleOnClose = () => {
    resetForm();
    setOpenIndices([0, 1, 2]);
    onClose();
  };

  useEffect(() => {
    register('affiliationDetails', {
      validate: value => {
        if (
          !value ||
          Object.keys(value).length === 0 ||
          !value.organizationName
        ) {
          return 'Primary affiliation is required';
        }
        return true;
      }
    });

    register('researchFocusAndExpertise.specialtyAndKeywords', {
      validate: value => {
        if (!isSpecialtyAndKeywordRequired) return true;
        if (
          !Array.isArray(value) ||
          value.length === 0 ||
          !value.some((item: any) => item?.specialty)
        ) {
          return 'At least one specialty is required';
        }
        return true;
      }
    });
  }, [register, isValid]);

  const formSections = [
    {
      title: 'Profile Details',
      content: (
        <ProfileDetails
          configs={configs}
          setCustomErrorMessage={setCustomErrorMessage}
          customErrorMessage={customErrorMessage}
          primaryEmail={userDetails?.user?.email ?? email ?? ''}
          isEditablePrimaryEmail={true}
        />
      )
    },
    {
      title: 'Affiliations',
      content: (
        <AffiliationSection configs={configs} organizationId={organizationId} />
      )
    },
    {
      title: 'Research Interest',
      content: <ResearchFocusAndExpertise configs={configs} />
    }
  ];

  useEffect(() => {
    const hasErrors = Object.keys(errors || {}).length > 0;
    if (isSubmitted && hasErrors) {
      setOpenIndices([0, 1, 2]);
      if (lastScrolledSubmit.current !== submitCount) {
        lastScrolledSubmit.current = submitCount;

        setTimeout(() => {
          scrollToFirstError();
        }, 300);
      }
    }
  }, [errors, isSubmitted, submitCount, scrollToFirstError]);

  return (
    <>
      {isOpen && (
        <Overlay overflow={'hidden'}>
          <NavbarDashboard />
          <Layout
            width="100%"
            paddingY={0}
            backgroundColor="surfaceSecondaryFill"
          >
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(() => handleCreateUser(false))}>
                <Flex flex="1" overflowY="auto" height={'80vh'}>
                  <Flex direction="column" width="80%" mx="auto">
                    <Flex gap={2} mt={10} mb={3} alignItems="baseline">
                      <FontAwesomeIcon icon={faUser} size="xl" />
                      <Text textStyle="headingSection">{title}</Text>
                    </Flex>
                    {isEditMode && isUserDetailsLoading ? (
                      <Text textStyle="textBody">Loading user details...</Text>
                    ) : (
                      <Accordion
                        chakraAccordionProps={{
                          allowMultiple: true,
                          allowToggle: true,
                          index: openIndices,
                          onChange: (indices: number | number[]) => {
                            setOpenIndices(
                              Array.isArray(indices) ? indices : [indices]
                            );
                          },
                          width: 'full',
                          background: 'surfacePrimaryFill',
                          marginBottom: 10
                        }}
                        items={formSections
                          .filter(item => item.title !== '')
                          .map(item => ({
                            background: 'surfacePrimaryFill',
                            title: (
                              <Flex direction="column">
                                <Text textStyle="headingItem">
                                  {item.title}
                                </Text>
                              </Flex>
                            ),
                            content: (
                              <Flex direction="column" gap={3}>
                                {item.content}
                              </Flex>
                            )
                          }))}
                      />
                    )}
                    {!isEditMode && !submissionContextId && (
                      <Flex direction="column" gap={5} pl={5} pb={50}>
                        <Flex gap={2} alignItems="center">
                          <Checkbox
                            id="addToReviewerSearchDatabase"
                            name="addToReviewerSearchDatabase"
                            ariaLabel="Add to Reviewer Search Database"
                            isChecked={addToReviewerSearchDatabase}
                            onChange={e => {
                              const checked = e.target.checked;
                              setValue('addToReviewerSearchDatabase', checked);
                              if (!checked) {
                                setValue('submissionContextIds', []);
                              } else if (singleJournalId) {
                                setValue('submissionContextIds', [
                                  singleJournalId
                                ]);
                              }
                            }}
                          >
                            Add to Reviewer Search Database
                          </Checkbox>
                          {singleJournalId && (
                            <Text>- {singleJournalName}</Text>
                          )}
                          <Tooltip
                            hasArrow
                            shouldWrapChildren
                            placement="top-start"
                            label="When enabled, the user is added to the Reviewer Search Database for the selected journal(s)."
                            borderRadius={4}
                            data-cy="info-tooltip"
                          >
                            <ButtonIcon
                              size="small"
                              ariaLabel="Reviewer Search Database info"
                              icon={<FontAwesomeIcon icon={faInfoCircle} />}
                              data-cy="info-icon"
                            />
                          </Tooltip>
                        </Flex>
                        {addToReviewerSearchDatabase && !singleJournalId && (
                          <Flex direction="column" gap={1} width="50%">
                            <ChakraMultiSelect
                              options={journalOptions}
                              value={submissionContextIds ?? []}
                              onChange={value =>
                                setValue('submissionContextIds', value, {
                                  shouldValidate: true
                                })
                              }
                              placeholder="Select journals"
                              variant="interactive"
                              showSelectAllOption
                              showSearch
                            />
                            {errors.submissionContextIds?.message && (
                              <FormFeedback variant="error">
                                {errors.submissionContextIds.message}
                              </FormFeedback>
                            )}
                          </Flex>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </Flex>
                <Flex
                  position="absolute"
                  bottom={0}
                  padding={4}
                  left={0}
                  right={0}
                  alignItems="center"
                  bgColor="surfacePrimaryFill"
                  shadow="md"
                  zIndex={10}
                >
                  <Flex width="100%" justifyContent="space-between">
                    <Button variant="caution" onClick={handleOnClose}>
                      Exit
                    </Button>
                    <Flex gap={2}>
                      <Button
                        variant="primary"
                        type="button"
                        iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
                        isDisabled={
                          addUserMutation?.isLoading ||
                          updateUserByAdminMutation?.isLoading ||
                          (isEditMode && !hasEditPayloadChanges) ||
                          (isEditMode && isUserDetailsLoading)
                        }
                        disabled={
                          addUserMutation?.isLoading ||
                          updateUserByAdminMutation?.isLoading ||
                          (isEditMode && !hasEditPayloadChanges) ||
                          (isEditMode && isUserDetailsLoading)
                        }
                        onClick={() =>
                          handleSubmit(() => handleCreateUser(false))()
                        }
                      >
                        {submitButtonText}
                      </Button>
                      <Button
                        variant="primary"
                        type="button"
                        iconLeft={<FontAwesomeIcon icon={faEnvelope} />}
                        isDisabled={
                          addUserMutation?.isLoading ||
                          updateUserByAdminMutation?.isLoading ||
                          (isEditMode && !hasEditPayloadChanges) ||
                          (isEditMode && isUserDetailsLoading)
                        }
                        disabled={
                          addUserMutation?.isLoading ||
                          updateUserByAdminMutation?.isLoading ||
                          (isEditMode && !hasEditPayloadChanges) ||
                          (isEditMode && isUserDetailsLoading)
                        }
                        onClick={() =>
                          handleSubmit(() => handleCreateUser(true))()
                        }
                      >
                        {isEditMode
                          ? 'Save and Notify User'
                          : 'Add and Notify User'}
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              </form>
            </FormProvider>
          </Layout>
        </Overlay>
      )}
    </>
  );
};

export default AddUser;
